import React, { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Button } from '@/design-system'
import { useStudio3DData } from '../../hooks/useStudio3DData'
import { useStudioEngine } from '../../hooks/useStudioEngine'
import { useMountainLotScene } from './useMountainLotScene'
import { LotPhoto } from './buildMountainLot'
import { monoLabelSx } from '../../components/shared'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

const FALLBACK_PHOTOS: LotPhoto[] = [
  { url: '/marketing/landing-v2/a03-mountains.jpg', ratio: 4 / 3 },
  { url: '/marketing/landing-v2/a02-beach.jpg', ratio: 3 / 4 },
]

/**
 * `/galleries/:galleryId/mountain-lot` — a first-person "living space" take:
 * floor-to-ceiling glass on two sides of a small cabin looking out at
 * low-poly peaks, a waterfall, and pines. WASD/arrows walk; the camera has
 * no wheel-zoom and gets clamped to the interior floor every frame, so
 * there's no way to leave the room and end up standing in the scenery.
 * Another "Spaces" idea prototype — reachable from the vision panel's Shop
 * layer, nothing here persists.
 */
export function MountainLotScreen() {
  const { galleryId = '' } = useParams<{ galleryId: string }>()
  const navigate = useNavigate()
  const data = useStudio3DData(galleryId)
  const ready = data.seedReady

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { scene } = useStudioEngine(canvasRef, ready)

  const photos: LotPhoto[] = useMemo(() => {
    const real = data.initialArts.filter((a) => !a.isPlaceholder && a.img).map((a) => ({ url: a.img, ratio: a.ratio || 4 / 3 }))
    return real.length > 0 ? real : FALLBACK_PHOTOS
  }, [data.initialArts])

  useMountainLotScene(scene, canvasRef, photos)

  const [showHint, setShowHint] = useState(true)
  const backToVision = () => {
    if (galleryId) navigate(`/galleries/${galleryId}/virtual-spaces`)
    else navigate('/galleries')
  }

  return (
    <Stage>
      <Canvas ref={canvasRef} id='mountain-lot-canvas' tabIndex={0} onPointerDown={() => setShowHint(false)} />

      {!ready && (
        <Box sx={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, background: '#0b0a10' }}>
          <Typography sx={{ ...monoLabelSx, fontSize: 9, color: 'rgba(244,243,246,0.7)' }}>LOADING…</Typography>
        </Box>
      )}

      <Top>
        <Brand>
          <LogoMark />
          <Typography component='span' sx={{ fontWeight: 750, letterSpacing: '-0.02em', fontSize: 15 }}>
            {APP_NAME}
          </Typography>
          <Typography component='span' sx={{ ...monoLabelSx, fontSize: 8, ml: 0.25 }}>
            MOUNTAIN LOT · SPACES PROTOTYPE
          </Typography>
        </Brand>
        <Button variant='night' size='sm' onClick={backToVision}>
          ← Back to vision
        </Button>
      </Top>

      {ready && showHint && (
        <Hint>
          <Typography sx={{ fontSize: 13, fontWeight: 650, mb: 0.5 }}>Walk the lot</Typography>
          <Typography sx={{ fontSize: 12, color: 'rgba(244,243,246,0.6)', lineHeight: 1.5 }}>
            <b>WASD</b> or <b>arrow keys</b> to walk, drag to look around. The glass wall is the edge of the room — you can’t
            walk (or zoom) through it into the scenery.
          </Typography>
        </Hint>
      )}
    </Stage>
  )
}

const Stage = styled(Box)({
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  background: '#050508',
  color: '#f4f3f6',
  fontFamily: 'var(--ds-font)',
})

const Canvas = styled('canvas')({
  position: 'fixed',
  inset: 0,
  width: '100%',
  height: '100%',
  outline: 'none',
  touchAction: 'none',
  display: 'block',
  cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
})

const Top = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '14px 18px',
})

const Brand = styled(Box)({ display: 'flex', alignItems: 'center', gap: 9 })

const Hint = styled(Box)({
  position: 'fixed',
  left: '50%',
  bottom: 28,
  transform: 'translateX(-50%)',
  zIndex: 20,
  maxWidth: 460,
  padding: '14px 18px',
  borderRadius: 14,
  background: 'rgba(14,13,18,0.82)',
  border: '1px solid rgba(244,243,246,0.14)',
  backdropFilter: 'blur(14px)',
  textAlign: 'center',
  pointerEvents: 'none',
})
